import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOnboardingSession } from '../../hooks/useOnboardingSession';
import { useToast } from '@/components/ui/use-toast';
import { Loader2 } from 'lucide-react';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

export const OnboardingGuard: React.FC<OnboardingGuardProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { session, isLoading, isError } = useOnboardingSession();

  useEffect(() => {
    if (isLoading || isError || !session) return;

    const isCurrentlyOnboarding = location.pathname.startsWith('/onboarding');
    // Settings (profile, account deletion, …) must stay reachable during onboarding —
    // the user menu links there from the onboarding top bar.
    const isSettingsPath = location.pathname.startsWith('/settings');
    const shouldOnboard = session.shouldOnboard === true;

    if (!shouldOnboard && isCurrentlyOnboarding) {
      // Non-owners are bounced out of a setup flow they never had access to, so
      // say why instead of leaving the redirect unexplained (DC-479).
      if (session.quickstartRestricted === true) {
        toast({
          title: 'Workspace setup is managed by organization owners',
          description: 'Your workspace is already set up — taking you to the dashboard.',
        });
      }
      navigate('/dashboard', { replace: true });
      return;
    }

    if (shouldOnboard && !isCurrentlyOnboarding && !isSettingsPath) {
      navigate(session.nextUrl ?? '/onboarding', { replace: true });
      return;
    }
  }, [session, isLoading, isError, location.pathname, navigate, toast]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // We always render children and let the effect handle redirects to avoid flickering
  // or blocking the app if the API is slow/down.
  return <>{children}</>;
};
